import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, MessageCircle, ScanLine, ReceiptText, Car, Calculator,
  FileSpreadsheet, Building2, PieChart, ChevronRight,
} from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import { WHATSAPP_LINK } from '../utils/constants';

const GROUPS = [
  {
    icon: MessageCircle, title: 'WhatsApp bookkeeping',
    items: [
      { label: 'Income tracking', desc: 'Text “got paid $1,200 by Acme” and it logs as categorized income.', to: '/whatsapp-bookkeeping' },
      { label: 'Expense tracking', desc: 'Text “$45 fuel” and MoneyFeed files it to the right expense line.', to: '/whatsapp-accounting' },
      { label: 'AI categorization', desc: 'Every entry is auto-mapped to a tax category — no manual sorting.', to: '/demo' },
    ],
  },
  {
    icon: ScanLine, title: 'Receipt & invoice management',
    items: [
      { label: 'Receipt scanning', desc: 'Snap a receipt or send a PDF — AI extracts the merchant, date, tax, invoice number and every line item — not just the total. Review items on WhatsApp and edit them from the review link.', to: '/schedule-c-receipt-scanner' },
      { label: 'Invoice scanning', desc: 'Bulk-scan up to 20 invoices/receipts at once.', to: '/invoice-scanner' },
      { label: 'Receipt archive', desc: 'Every receipt gets its own MF number (MF-####), and every original image is stored and linked for your audit trail.', to: '/scan-receipts-to-pdf' },
    ],
  },
  {
    icon: Calculator, title: 'Tax',
    items: [
      { label: 'Tax categories & Schedule C', desc: 'US IRS Schedule C, UK Self Assessment, Canada T2125 and VAT lines.', to: '/tax-support' },
      { label: 'Tax estimates & reminders', desc: 'See how much to set aside for quarterly (1040-ES) and year-end tax.', to: '/tools/1099-tax-calculator' },
      { label: 'Tax-ready reports', desc: 'Export CPA-ready summaries whenever you or your accountant file.', to: '/for-accountants' },
      { label: 'CPA-Ready Tax Packet', desc: 'Your records, converted into IRS form layout (Schedule C and Schedule SE) with quarterly estimates — a ready packet to hand to your CPA. MoneyFeed does not file your taxes; it makes filing easy for you and your accountant.', to: '/for-accountants' },
    ],
  },
  {
    icon: Car, title: 'Mileage',
    items: [
      { label: 'Mileage tracking', desc: 'Log business trips and apply the current standard mileage rate.', to: '/demo' },
      { label: 'Mileage deductions', desc: 'Trips roll into your deductible expenses automatically.', to: '/blog/mileage-deduction-2026' },
    ],
  },
  {
    icon: PieChart, title: 'Reporting',
    items: [
      { label: 'Profit & loss', desc: 'A live P&L updates as you text income and expenses.', to: '/demo' },
      { label: 'CSV & Excel export', desc: 'Download clean, accountant-ready spreadsheets in one click.', to: '/export-to-excel' },
      { label: 'PDF reports', desc: 'Export a categorized PDF report or receipt binder.', to: '/scan-receipts-to-pdf' },
    ],
  },
  {
    icon: Building2, title: 'Business',
    items: [
      { label: 'Team expense capture', desc: 'Up to 5 people text expenses into one company account.', to: '/business' },
      { label: 'Accountant workflow', desc: 'Hand bookkeepers clean, tax-ready client data.', to: '/for-accountants' },
      { label: 'Bulk receipt scanning', desc: 'Scan a whole pile of receipts in minutes with AI.', to: '/invoice-scanner' },
    ],
  },
  {
    icon: ReceiptText, title: 'Invoicing',
    items: [
      { label: 'Invoice creation', desc: 'Create branded invoices with line items and the right tax/VAT.', to: '/invoicing' },
      { label: 'Payment status', desc: 'Share invoices on WhatsApp and track what has been paid.', to: '/invoicing' },
      { label: 'Automatic income recording', desc: 'A paid invoice auto-logs as income in your books.', to: '/invoicing' },
    ],
  },
];

const FAQS = [
  { q: 'What can MoneyFeed do?', a: 'MoneyFeed does WhatsApp bookkeeping (income and expense tracking with AI categorization), receipt and invoice scanning with an archived audit trail, mileage tracking, tax categorization and estimates, invoicing, and accountant-ready reporting exported as PDF, CSV or Excel.' },
  { q: 'Do I need to install an app to use these features?', a: 'No. The core bookkeeping features work entirely inside WhatsApp. A web dashboard is available for reviewing reports, bulk scanning and exports, but there is no separate app to learn.' },
  { q: 'Which features are on the free plan?', a: 'The free plan includes WhatsApp income and expense tracking, AI categorization, a live dashboard, the free tax calculator and a limited number of receipt scans to try. Higher plans add more receipt scanning, tax reports, invoicing and team features.' },
];

export default function Features() {
  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'MoneyFeed',
      applicationCategory: 'FinanceApplication', operatingSystem: 'iOS, Android, Web',
      url: 'https://moneyfeed.io/features',
      description: 'MoneyFeed is a WhatsApp expense capture tool for freelancers and small businesses: expense and income tracking, receipt scanning, mileage, tax reports and invoicing.',
      featureList: GROUPS.flatMap((g) => g.items.map((i) => i.label)),
    },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="features-page">
      <Seo
        title="MoneyFeed Features — Your Bookkeeping App on WhatsApp"
        description="Everything MoneyFeed does: WhatsApp expense & income tracking, AI categorization, receipt scanning, mileage, tax-ready reports, CSV/Excel export and invoicing."
        path="/features"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="features-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">Features</span>
      </nav>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Everything MoneyFeed does
        </h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto mb-4">
          <strong>MoneyFeed is the WhatsApp tool for freelancers and small businesses — it works with the bookkeeping app you already use.</strong> Track income and expenses, scan receipts, log mileage, and get tax-ready reports — all from a chat.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="features-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="features-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-10">
        {GROUPS.map((g) => (
          <div key={g.title} data-testid={`features-group-${g.title}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#064E3B] flex items-center justify-center">
                <g.icon className="h-5 w-5 text-[#84CC16]" />
              </div>
              <h2 className="text-2xl font-bold text-[#064E3B]">{g.title}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {g.items.map((i) => (
                <Link key={i.label} to={i.to} className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg hover:border-[#84CC16] border border-transparent transition-all" data-testid={`feature-${i.label}`}>
                  <h3 className="font-bold text-[#064E3B] mb-1 flex items-center gap-1">{i.label} <ArrowRight className="h-3.5 w-3.5 opacity-50" /></h3>
                  <p className="text-gray-600 text-sm">{i.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Feature FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`features-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">See it all in action</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Watch MoneyFeed turn a WhatsApp message into tax-ready books, live.</p>
          <Link to="/demo">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="features-cta-demo">
              See the demo <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/about-moneyfeed" className="hover:text-[#064E3B]">About</Link>
          <Link to="/supported-countries" className="hover:text-[#064E3B]">Supported Countries</Link>
          <Link to="/tax-support" className="hover:text-[#064E3B]">Tax Support</Link>
          <Link to="/security" className="hover:text-[#064E3B]">Security</Link>
        </div>
      </footer>
    </div>
  );
}
